/**
 * Mixpanel active backfill pipeline wizard for /admin/conversion-roi.
 *
 * Mirrors PostHogPipelineWizard — operators paste Service Account creds
 * + project ID → encrypted via Fernet → Backfill button pulls events
 * via the Raw Event Export API (NDJSON) and upserts to conversion_events.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Plug, CheckCircle2, AlertTriangle, RefreshCw, Trash2, ShieldCheck, ExternalLink, History } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ProbeBadge({ state }) {
  if (state === 'idle') return null;
  if (state === 'testing') return <Badge className="bg-slate-700/60 text-slate-200">Testing…</Badge>;
  if (state === 'ok') return <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Connected</Badge>;
  return <Badge className="bg-rose-500/20 text-rose-200 border border-rose-400/40"><AlertTriangle className="w-3 h-3 inline mr-1" /> Failed</Badge>;
}

export default function MixpanelPipelineWizard({ onBackfillComplete }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    region_host: 'https://data.mixpanel.com',
    project_id: '',
    service_account_username: '',
    service_account_secret: '',
  });
  const [probe, setProbe] = useState({ state: 'idle', msg: null });
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/mixpanel-pipeline/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setConfig(d.connected ? d : null);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const callApi = async (path, method, body) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const text = await r.text();
    let json = null;
    try { json = JSON.parse(text); } catch { /* non-json */ }
    return { ok: r.ok, status: r.status, body: json || text };
  };

  const payload = () => ({
    region_host: form.region_host,
    project_id: Number(form.project_id),
    service_account_username: form.service_account_username.trim(),
    service_account_secret: form.service_account_secret,
  });

  const test = async () => {
    setProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/mixpanel-pipeline/test', 'POST', payload());
    if (r.ok && r.body.ok) {
      setProbe({ state: 'ok', msg: `Reachable · project ${r.body.project_id}` });
    } else {
      setProbe({ state: 'fail', msg: r.body?.error || r.body?.detail || `HTTP ${r.status}` });
    }
  };

  const connect = async () => {
    setProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/mixpanel-pipeline/connect', 'POST', payload());
    if (r.ok) {
      setProbe({ state: 'ok', msg: 'Connected' });
      setForm({ region_host: 'https://data.mixpanel.com', project_id: '', service_account_username: '', service_account_secret: '' });
      refresh();
    } else setProbe({ state: 'fail', msg: r.body?.detail || `HTTP ${r.status}` });
  };

  const backfill = async (days) => {
    setBackfillBusy(true);
    setBackfillResult(null);
    const r = await callApi(`/api/mixpanel-pipeline/backfill?days=${days}`, 'POST');
    setBackfillBusy(false);
    if (r.ok) {
      setBackfillResult(r.body);
      onBackfillComplete?.(r.body);
      refresh();
    } else setBackfillResult({ error: r.body?.detail || `HTTP ${r.status}` });
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Mixpanel? Encrypted secret will be deleted. Already-imported conversion_events are kept.')) return;
    await callApi('/api/mixpanel-pipeline/config', 'DELETE');
    refresh();
    setBackfillResult(null);
  };

  const canSubmit = form.project_id && form.service_account_username && form.service_account_secret;

  return (
    <Card className="bg-slate-950 border-emerald-500/30" data-testid="mixpanel-pipeline-wizard">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Plug className="w-4 h-4 text-emerald-400" />
          Mixpanel active pipeline — auto-import conversion events
          <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 text-[9px] font-mono tracking-widest ml-1">P1</Badge>
        </CardTitle>
        <p className="text-sm text-slate-200 mt-1">Service-account credentials (username + secret) pull events from the Mixpanel Raw Event Export API. Same encrypted-vault as PostHog/cloud creds.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {config ? (
          <div className="rounded-md bg-slate-900 border border-emerald-500/30 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <ShieldCheck className="w-4 h-4 text-emerald-300" />
                  <span className="text-white font-bold">Connected</span>
                  <code className="text-xs text-emerald-300 font-mono">project {config.project_id}</code>
                  <code className="text-[10px] text-slate-300 font-mono">{config.region_host}</code>
                </div>
                <div className="text-xs text-slate-300">
                  Service account: <code className="text-emerald-300">{config.masked_username}</code>
                </div>
                {config.last_backfill_at && (
                  <div className="text-xs text-slate-300 mt-0.5">
                    Last backfill: {new Date(config.last_backfill_at).toLocaleString()} · {config.last_backfill_count} events
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => backfill(7)} disabled={backfillBusy} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs" data-testid="mixpanel-backfill-7d-btn">
                  {backfillBusy ? 'Syncing…' : <><RefreshCw className="w-3 h-3 mr-1" /> Backfill 7d</>}
                </Button>
                <Button size="sm" onClick={() => backfill(30)} disabled={backfillBusy} className="bg-emerald-700 hover:bg-emerald-600 text-white h-8 text-xs" data-testid="mixpanel-backfill-30d-btn">
                  <History className="w-3 h-3 mr-1" /> 30d
                </Button>
                <Button size="sm" variant="ghost" onClick={disconnect} className="text-rose-300 hover:text-rose-200 h-8 px-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            {backfillResult && (
              <div className="mt-3 text-xs">
                {backfillResult.error ? (
                  <div className="text-rose-300">{backfillResult.error}</div>
                ) : (
                  <div className="text-emerald-300">
                    ✓ {backfillResult.events_fetched} fetched · {backfillResult.events_upserted} imported · {backfillResult.events_skipped} skipped · window {backfillResult.window_days}d
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-md bg-slate-900 border border-emerald-500/20 p-3" data-testid="mixpanel-form">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold text-white">Mixpanel service-account credentials</div>
              <ProbeBadge state={probe.state} />
            </div>
            <select
              value={form.region_host}
              onChange={(e) => setForm({ ...form, region_host: e.target.value })}
              className="w-full mb-2 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-white"
              data-testid="mixpanel-host-select"
            >
              <option value="https://data.mixpanel.com">US Cloud (data.mixpanel.com)</option>
              <option value="https://data-eu.mixpanel.com">EU Cloud (data-eu.mixpanel.com)</option>
            </select>
            <input
              type="number"
              placeholder="Project ID (e.g., 1234567)"
              value={form.project_id}
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              className="w-full mb-2 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-white"
              data-testid="mixpanel-project-id-input"
            />
            <input
              type="text"
              placeholder="Service account username (e.g., cartie-exporter.123.abcde)"
              value={form.service_account_username}
              onChange={(e) => setForm({ ...form, service_account_username: e.target.value })}
              className="w-full mb-2 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-white"
              data-testid="mixpanel-username-input"
            />
            <input
              type="password"
              placeholder="Service account secret"
              value={form.service_account_secret}
              onChange={(e) => setForm({ ...form, service_account_secret: e.target.value })}
              className="w-full mb-2 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-white"
              data-testid="mixpanel-secret-input"
            />
            <div className="flex gap-2 mt-1 flex-wrap">
              <Button size="sm" variant="outline" onClick={test} disabled={!canSubmit} className="text-xs h-7 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="mixpanel-test-btn">Test</Button>
              <Button size="sm" onClick={connect} disabled={!canSubmit} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-7" data-testid="mixpanel-connect-btn">Save & connect</Button>
              <a href="https://docs.mixpanel.com/docs/orgs-and-projects/service-accounts" target="_blank" rel="noreferrer" className="ml-auto text-xs text-slate-200 hover:text-emerald-300 flex items-center gap-1">
                Create a Service Account <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            {probe.msg && <div className={`mt-2 text-xs ${probe.state === 'ok' ? 'text-emerald-300' : 'text-rose-300'}`}>{probe.msg}</div>}
            <div className="text-[10px] text-slate-300 italic mt-2">
              We track: <code className="text-emerald-300">Sign Up</code>, <code className="text-emerald-300">Purchase Completed</code>, <code className="text-emerald-300">Subscription Started</code>, <code className="text-emerald-300">Upgrade</code>, <code className="text-emerald-300">Churn</code> (and common synonyms).
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
